import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Bundle Discount API Component
 * Handles automatic discount application for bundle products in cart and checkout
 */
export class BundleDiscountAPIComponent extends Component {
  requiredRefs = [];

  /** @type {{
    bundleDiscountCode: string | null,
    isDiscountApplied: boolean,
    bundleProducts: Set<string>,
    bundleCombinations: Map<string, Set<string>>,
    discountPercentage: number
  }} */
  #bundleData = {
    bundleDiscountCode: null,
    isDiscountApplied: false,
    bundleProducts: new Set(),
    bundleCombinations: new Map(), // Track which products belong to which bundles
    discountPercentage: 0
  };

  connectedCallback() {
    super.connectedCallback();
    this.#initializeBundleDiscount();
    this.#bindEvents();
  }

  /**
   * Initialize bundle discount data
   */
  async #initializeBundleDiscount() {
    try {
      // Get bundle discount code from shop metafields or settings
      const response = await fetch('/meta.json');
      if (response.ok) {
        const metaData = await response.json();
        this.#bundleData.bundleDiscountCode = metaData.shop?.metafields?.custom?.bundle_discount_code?.value || 'BUNDLE_SAVE';
        this.#bundleData.discountPercentage = metaData.shop?.metafields?.custom?.bundle_discount_percentage?.value || 35;
      }

      // Check if discount is already applied
      await this.#checkDiscountStatus();
    } catch (error) {
      console.error('Error initializing bundle discount:', error);
    }
  }

  /**
   * Bind event listeners
   */
  #bindEvents() {
    // Listen for cart updates
    document.addEventListener(ThemeEvents.CART_ADD, this.#handleCartUpdate.bind(this));
    document.addEventListener(ThemeEvents.CART_UPDATE, this.#handleCartUpdate.bind(this));
    document.addEventListener(ThemeEvents.CART_REMOVE, this.#handleCartUpdate.bind(this));

    // Listen for bundle product additions
    document.addEventListener('bundle-added-to-cart', this.#handleBundleAdded.bind(this));
  }

  /**
   * Handle cart updates
   */
  async #handleCartUpdate(event) {
    const cart = event.detail?.cart;
    if (cart) {
      await this.#processCartForBundleDiscount(cart);
    }
  }

  /**
   * Handle bundle product additions
   */
  async #handleBundleAdded(event) {
    const bundleProducts = event.detail?.bundleProducts || [];
    const bundleId = event.detail?.bundleId || `bundle_${Date.now()}`;
    
    if (bundleProducts.length > 0) {
      // Add bundle products to tracking set
      bundleProducts.forEach(product => {
        this.#bundleData.bundleProducts.add(product.id.toString());
      });

      // Track this specific bundle combination
      const productIds = bundleProducts.map(product => product.id.toString());
      this.#bundleData.bundleCombinations.set(bundleId, new Set(productIds));

      // Process cart for discount
      const cart = await this.#getCurrentCart();
      if (cart) {
        await this.#processCartForBundleDiscount(cart);
      }
    }
  }

  /**
   * Get current cart data
   */
  async #getCurrentCart() {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
    return null;
  }

  /**
   * Process cart to check for bundle discount eligibility
   */
  async #processCartForBundleDiscount(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return;
    }

    // Check if cart contains both bundle products (bundle purchase)
    const bundleEligibility = this.#checkBundleEligibility(cart.items);
    
    if (bundleEligibility.isEligible && !this.#bundleData.isDiscountApplied) {
      await this.#applyBundleDiscount();
    } else if (!bundleEligibility.isEligible && this.#bundleData.isDiscountApplied) {
      await this.#removeBundleDiscount();
    }
  }

  /**
   * Check if cart contains bundle products (bundle purchase)
   */
  #checkBundleEligibility(cartItems) {
    // Get all bundle products in the cart that were added as part of a bundle
    const bundleItems = cartItems.filter(item => {
      return this.#isBundleProduct(item);
    });

    // Group items by bundle ID to check for complete bundles
    const bundleGroups = new Map();
    
    bundleItems.forEach(item => {
      const bundleId = item.properties?._bundle_id;
      if (bundleId) {
        if (!bundleGroups.has(bundleId)) {
          bundleGroups.set(bundleId, []);
        }
        bundleGroups.get(bundleId).push(item);
      }
    });

    // Check if we have complete bundles (at least 2 products per bundle)
    const completeBundles = [];
    for (const [bundleId, items] of bundleGroups) {
      if (items.length >= 2) {
        completeBundles.push({
          bundleId: bundleId,
          items: items,
          productCount: new Set(items.map(item => item.product_id)).size
        });
      }
    }

    // Check if we have at least one complete bundle with 2+ different products
    const hasCompleteBundle = completeBundles.some(bundle => bundle.productCount >= 2);
    
    return {
      isEligible: hasCompleteBundle,
      bundleItems: bundleItems,
      completeBundles: completeBundles,
      uniqueProductCount: new Set(bundleItems.map(item => item.product_id)).size
    };
  }

  /**
   * Check if a product is a bundle product
   */
  #isBundleProduct(item) {
    // Only consider items that were explicitly added as bundle products
    // This prevents individual products from getting bundle discounts
    return item.properties?._bundle_product === 'true';
  }

  /**
   * Apply bundle discount to cart
   */
  async #applyBundleDiscount() {
    if (!this.#bundleData.bundleDiscountCode) {
      console.warn('Bundle discount code not configured');
      return;
    }

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount_code: this.#bundleData.bundleDiscountCode
        })
      });

      if (response.ok) {
        const cartData = await response.json();
        this.#bundleData.isDiscountApplied = true;
        
        // Trigger cart update event
        this.dispatchEvent(new CustomEvent(ThemeEvents.CART_UPDATE, {
          detail: { cart: cartData },
          bubbles: true
        }));

        console.log('Bundle discount applied successfully');
      } else {
        console.error('Failed to apply bundle discount');
      }
    } catch (error) {
      console.error('Error applying bundle discount:', error);
    }
  }

  /**
   * Remove bundle discount from cart
   */
  async #removeBundleDiscount() {
    try {
      // Get current cart to check for applied discounts
      const cart = await this.#getCurrentCart();
      if (cart && cart.discount_applications) {
        const bundleDiscount = cart.discount_applications.find(
          discount => discount.type === 'discount_code' && 
                     discount.code === this.#bundleData.bundleDiscountCode
        );

        if (bundleDiscount) {
          // Remove the discount by updating cart without the discount code
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              discount_code: null
            })
          });

          if (response.ok) {
            const cartData = await response.json();
            this.#bundleData.isDiscountApplied = false;
            
            // Trigger cart update event
            this.dispatchEvent(new CustomEvent(ThemeEvents.CART_UPDATE, {
              detail: { cart: cartData },
              bubbles: true
            }));

            console.log('Bundle discount removed successfully');
          }
        }
      }
    } catch (error) {
      console.error('Error removing bundle discount:', error);
    }
  }

  /**
   * Check current discount status
   */
  async #checkDiscountStatus() {
    const cart = await this.#getCurrentCart();
    if (cart && cart.discount_applications) {
      this.#bundleData.isDiscountApplied = cart.discount_applications.some(
        discount => discount.type === 'discount_code' && 
                   discount.code === this.#bundleData.bundleDiscountCode
      );
    }
  }

  /**
   * Create automatic discount code (for admin use)
   */
  async #createBundleDiscountCode() {
    // This would typically be done through Shopify Admin API
    // For now, we'll use a predefined discount code
    const discountCode = {
      code: this.#bundleData.bundleDiscountCode,
      type: 'percentage',
      value: this.#bundleData.discountPercentage,
      applies_to: {
        product_ids: Array.from(this.#bundleData.bundleProducts)
      },
      usage_limit: null,
      customer_selection: 'all',
      starts_at: new Date().toISOString(),
      ends_at: null
    };

    console.log('Bundle discount code configuration:', discountCode);
  }

  /**
   * Validate bundle discount eligibility
   */
  #validateBundleEligibility(cartItems) {
    // Check if cart contains at least 2 bundle products
    const bundleItems = cartItems.filter(item => this.#isBundleProduct(item));
    return bundleItems.length >= 2;
  }

  /**
   * Calculate bundle discount amount
   */
  #calculateBundleDiscount(cartItems) {
    const bundleItems = cartItems.filter(item => this.#isBundleProduct(item));
    const totalBundleValue = bundleItems.reduce((sum, item) => {
      return sum + (item.final_price * item.quantity);
    }, 0);

    return (totalBundleValue * this.#bundleData.discountPercentage) / 100;
  }
}

// Register the component
customElements.define('bundle-discount-api', BundleDiscountAPIComponent);
